import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Monitor } from 'lucide-react';

interface Slide {
    src: string;
    title: string;
    description: string;
}

const slides: Slide[] = [
    {
        src: '/screenshots/screenshot-01.png',
        title: 'Dashboard Principal',
        description: 'Vista centralizada de todos tus proyectos, métricas y estado de cobertura en tiempo real.',
    },
    {
        src: '/screenshots/ingesta.png',
        title: 'Ingesta de Datos',
        description: 'Conecta tus fuentes (Jira, PDF, Confluence). La IA entiende tus reglas de negocio mediante RAG privado y seguro.',
    },
    {
        src: '/screenshots/screenshot-06.png',
        title: 'Oráculos de Validación',
        description: 'Reglas de negocio y oráculos explícitos que garantizan que la IA razone con criterio, sin alucinaciones.',
    },
    {
        src: '/screenshots/screenshot-03.png',
        title: 'Casos de Prueba Generados',
        description: 'Casos positivos, negativos y de borde con criterios de aceptación claros y trazabilidad completa.',
    },
    {
        src: '/screenshots/screenshot-02.png',
        title: 'Casos de Prueba en Gherkin',
        description: 'Escenarios generados en formato Gherkin listos para integrarse con tu framework de automatización.',
    },
    {
        src: '/screenshots/screenshot-04.png',
        title: 'Automatización y FlowSpecQA Runner',
        description: 'Ejecución de pruebas Web y Mobile en tiempo real con logs, capturas y trazas integradas — todo desde la plataforma.',
    },
    {
        src: '/screenshots/screenshot-05.png',
        title: 'Historial de Ejecuciones',
        description: 'Seguimiento completo de cada corrida: resultados, tendencias y comparativas entre versiones.',
    },
];

const AUTOPLAY_INTERVAL = 4000;

const AppCarousel: React.FC = () => {
    const [current, setCurrent] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({});

    const prev = useCallback(() => {
        setCurrent(c => (c === 0 ? slides.length - 1 : c - 1));
    }, []);

    const next = useCallback(() => {
        setCurrent(c => (c === slides.length - 1 ? 0 : c + 1));
    }, []);

    useEffect(() => {
        if (isHovered) return;
        const timer = setInterval(next, AUTOPLAY_INTERVAL);
        return () => clearInterval(timer);
    }, [isHovered, next]);

    const handleImageError = (index: number) => {
        setImageErrors(prev => ({ ...prev, [index]: true }));
    };

    return (
        <section id="plataforma" className="py-24 px-4 bg-background-dark/40 border-y border-white/5">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-bold mb-6 font-montserrat tracking-widest uppercase">
                        <Monitor className="w-4 h-4" />
                        Plataforma en Acción
                    </div>
                    <h2 className="text-3xl md:text-5xl font-bold tracking-tight font-montserrat mb-4">
                        Diseñado para equipos que no pueden permitirse errores.
                    </h2>
                    <p className="text-slate-400 max-w-xl mx-auto">
                        Una interfaz construida por QA Engineers, para QA Engineers.
                    </p>
                </div>

                {/* Carousel */}
                <div
                    className="relative"
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    {/* Main image area */}
                    <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-primary/10 bg-slate-900/60 aspect-video">
                        {slides.map((slide, i) => (
                            <div
                                key={i}
                                className={`absolute inset-0 transition-opacity duration-700 ${i === current ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
                            >
                                {imageErrors[i] ? (
                                    /* Placeholder mientras no hay imagen */
                                    <div className="w-full h-full flex flex-col items-center justify-center gap-4 bg-slate-900/80">
                                        <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                                            <Monitor className="w-8 h-8 text-primary/40" />
                                        </div>
                                        <p className="text-slate-600 text-sm font-mono">{slide.src}</p>
                                    </div>
                                ) : (
                                    <img
                                        src={slide.src}
                                        alt={slide.title}
                                        className="w-full h-full object-cover"
                                        onError={() => handleImageError(i)}
                                    />
                                )}
                                {/* Gradient overlay bottom */}
                                <div className="absolute inset-0 bg-gradient-to-t from-background-dark/80 via-transparent to-transparent pointer-events-none" />
                            </div>
                        ))}

                        {/* Prev / Next buttons */}
                        <button
                            onClick={prev}
                            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-background-dark/70 border border-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/10 transition-all"
                            aria-label="Anterior"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                            onClick={next}
                            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-background-dark/70 border border-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/10 transition-all"
                            aria-label="Siguiente"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Caption */}
                    <div className="mt-6 text-center min-h-[56px] transition-all duration-300">
                        <h3 className="font-bold text-lg font-montserrat text-white mb-1">
                            {slides[current].title}
                        </h3>
                        <p className="text-slate-400 text-sm max-w-xl mx-auto">
                            {slides[current].description}
                        </p>
                    </div>

                    {/* Dots */}
                    <div className="flex justify-center gap-2 mt-6">
                        {slides.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => setCurrent(i)}
                                aria-label={`Ir a slide ${i + 1}`}
                                className={`transition-all duration-300 rounded-full ${
                                    i === current
                                        ? 'w-6 h-2 bg-primary'
                                        : 'w-2 h-2 bg-white/20 hover:bg-white/40'
                                }`}
                            />
                        ))}
                    </div>

                    {/* Progress bar */}
                    {!isHovered && (
                        <div className="mt-4 max-w-xs mx-auto h-px bg-white/5 rounded overflow-hidden">
                            <div
                                key={current}
                                className="h-full bg-primary/50 rounded"
                                style={{
                                    animation: `progress ${AUTOPLAY_INTERVAL}ms linear forwards`,
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                @keyframes progress {
                    from { width: 0%; }
                    to   { width: 100%; }
                }
            `}</style>
        </section>
    );
};

export default AppCarousel;
